import React, { FC, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Collapse, IconButton, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import { TabHeader } from "../../../components/Tabs/TabHeader";
import { usePricing } from "./PricingContext";
import { DotLottleLoader } from "../../../components/Loader";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import ContentPlacementModal from "./components/ContentPlacementModal";
import ContentPlacementCategoryModal from "./components/ContentPlacementCategoryModal";

interface Placement {
    id: number;
    name: string;
    content_channel_id?: number | string;
    content_placement_category_id?: number | null;
    content_channel?: { id: number; name: string };
    category?: { id: number; name: string };
}

interface PlacementCategory {
    id: number;
    name: string;
    rmn?: boolean;
}

interface CategoryGroup {
    category: PlacementCategory | null; // null = uncategorized bucket
    placements: Placement[];
}

const UNCATEGORIZED_KEY = "uncategorized";

// Sub-views split by the category rmn flag ("Network" categories = RMN).
const VIEW_TABS = [
    { value: 0, text: "Standard" },
    { value: 1, text: "RMN" }
];

// Single hierarchical view: placement categories as collapsible groups with
// their placements nested beneath; placements without a category collect in
// an "Uncategorized" bucket (shown on the Standard view).
export const PlacementsTab: FC = () => {
    const { canEdit } = usePricing();
    const [categories, setCategories] = useState<PlacementCategory[]>([]);
    const [placements, setPlacements] = useState<Placement[]>([]);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState(0);
    const [searchText, setSearchText] = useState("");
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

    const [showPlacementModal, setShowPlacementModal] = useState(false);
    const [editingPlacement, setEditingPlacement] = useState<Partial<Placement> | null>(null);
    const [placementToDelete, setPlacementToDelete] = useState<Placement | null>(null);

    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState<PlacementCategory | null>(null);
    const [categoryToDelete, setCategoryToDelete] = useState<PlacementCategory | null>(null);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const [catRes, plRes] = await Promise.all([
                axiosInstant.get("/content-placement-categories"),
                axiosInstant.get("/content-placements")
            ]);
            setCategories(Array.isArray(catRes.data) ? catRes.data : catRes.data?.rows || []);
            setPlacements(Array.isArray(plRes.data) ? plRes.data : plRes.data?.rows || []);
        } catch (err) {
            toast.error("Failed to fetch placements.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const groups: CategoryGroup[] = useMemo(() => {
        const search = searchText.toLowerCase();
        const isRmnView = view === 1;
        const viewCategories = categories.filter((c) => !!c.rmn === isRmnView);
        const byCategory = new Map<number, Placement[]>();
        const uncategorized: Placement[] = [];
        for (const p of placements) {
            const catId = p.content_placement_category_id ?? p.category?.id ?? null;
            if (catId && categories.some((c) => c.id === catId)) {
                byCategory.set(catId, [...(byCategory.get(catId) || []), p]);
            } else {
                uncategorized.push(p);
            }
        }
        const sortByName = (a: { name: string }, b: { name: string }) =>
            a.name.localeCompare(b.name);
        const all: CategoryGroup[] = [...viewCategories].sort(sortByName).map((category) => ({
            category,
            placements: (byCategory.get(category.id) || []).sort(sortByName)
        }));
        // Uncategorized placements carry no rmn flag — surface them on Standard.
        if (!isRmnView && uncategorized.length > 0) {
            all.push({ category: null, placements: uncategorized.sort(sortByName) });
        }
        if (!search) return all;
        // Category name match keeps the whole group; otherwise keep only
        // matching placements and drop groups with no hits.
        return all
            .map((g) => {
                if (g.category?.name.toLowerCase().includes(search)) return g;
                return {
                    ...g,
                    placements: g.placements.filter((p) => p.name?.toLowerCase().includes(search))
                };
            })
            .filter((g) => g.placements.length > 0 || g.category?.name.toLowerCase().includes(search));
    }, [categories, placements, searchText, view]);

    const groupKey = (g: CategoryGroup) => (g.category ? String(g.category.id) : UNCATEGORIZED_KEY);

    const toggleGroup = (key: string) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleSavePlacement = async (data: any) => {
        try {
            if (editingPlacement?.id) {
                await axiosInstant.patch(`/content-placements/${editingPlacement.id}`, data);
                toast.success("Placement updated.");
            } else {
                await axiosInstant.post("/content-placements", data);
                toast.success("Placement created.");
            }
            setShowPlacementModal(false);
            await fetchAll();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Error saving placement.");
            throw e;
        }
    };

    const handleDeletePlacement = async () => {
        if (!placementToDelete) return;
        const id = placementToDelete.id;
        setPlacementToDelete(null);
        try {
            await axiosInstant.delete(`/content-placements/${id}`);
            toast.success("Deleted successfully.");
            await fetchAll();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Error deleting placement.");
        }
    };

    const handleSaveCategory = async (data: any) => {
        try {
            if (editingCategory?.id) {
                await axiosInstant.patch(`/content-placement-categories/${editingCategory.id}`, data);
                toast.success("Category updated.");
            } else {
                await axiosInstant.post("/content-placement-categories", data);
                toast.success("Category created.");
            }
            setShowCategoryModal(false);
            await fetchAll();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Error saving category.");
        }
    };

    const handleDeleteCategory = async () => {
        if (!categoryToDelete) return;
        const id = categoryToDelete.id;
        setCategoryToDelete(null);
        try {
            await axiosInstant.delete(`/content-placement-categories/${id}`);
            toast.success("Deleted successfully.");
            await fetchAll();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Error deleting category.");
        }
    };

    if (loading && categories.length === 0 && placements.length === 0) {
        return <DotLottleLoader />;
    }

    return (
        <Box>
            <Box className="flex flex-wrap items-center justify-between gap-3" sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Placements
                </Typography>
                {canEdit && (
                    <Box className="flex gap-2">
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setEditingCategory(null);
                                setShowCategoryModal(true);
                            }}
                        >
                            Add Category
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setEditingPlacement(null);
                                setShowPlacementModal(true);
                            }}
                        >
                            Add Placement
                        </Button>
                    </Box>
                )}
            </Box>
            <Box className="flex flex-wrap items-center justify-between gap-3" sx={{ mb: 2 }}>
                <TabHeader
                    tabs={VIEW_TABS}
                    selectedTab={view}
                    setSelectedTab={setView}
                    variant="pill"
                />
                <TextField
                    size="small"
                    placeholder="Search placements and categories"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    sx={{ width: 333 }}
                />
            </Box>

            <Box className="flex flex-col gap-3">
                {groups.map((group) => {
                    const key = groupKey(group);
                    // While searching, always show groups expanded so hits are visible.
                    const isOpen = searchText ? true : !collapsed.has(key);
                    return (
                        <Box
                            key={key}
                            sx={{ border: "1px solid var(--border)", borderRadius: "12px", overflow: "hidden" }}
                        >
                            <Box
                                className="flex items-center justify-between gap-3 px-4 py-3"
                                sx={{ backgroundColor: "var(--surface-2)", cursor: "pointer" }}
                                onClick={() => toggleGroup(key)}
                            >
                                <Box className="flex items-center gap-2 min-w-0">
                                    {isOpen ? (
                                        <ExpandMoreIcon fontSize="small" sx={{ color: "var(--accent)" }} />
                                    ) : (
                                        <ChevronRightIcon fontSize="small" sx={{ color: "var(--accent)" }} />
                                    )}
                                    <Typography sx={{ fontWeight: 700 }} className="capitalize">
                                        {group.category?.name ?? "Uncategorized"}
                                    </Typography>
                                    {group.category?.rmn && (
                                        <Chip label="Network" size="small" variant="outlined" />
                                    )}
                                    <Chip
                                        label={`${group.placements.length} placement${
                                            group.placements.length !== 1 ? "s" : ""
                                        }`}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>
                                {canEdit && (
                                    <Box
                                        className="flex items-center gap-1"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <IconButton
                                            size="small"
                                            title={
                                                group.category
                                                    ? `Add placement to ${group.category.name}`
                                                    : "Add placement"
                                            }
                                            onClick={() => {
                                                setEditingPlacement({
                                                    content_placement_category_id: group.category?.id ?? null
                                                });
                                                setShowPlacementModal(true);
                                            }}
                                        >
                                            <AddIcon fontSize="small" />
                                        </IconButton>
                                        {group.category && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    title="Edit category"
                                                    onClick={() => {
                                                        setEditingCategory(group.category);
                                                        setShowCategoryModal(true);
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    title="Delete category"
                                                    onClick={() => setCategoryToDelete(group.category)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        )}
                                    </Box>
                                )}
                            </Box>
                            <Collapse in={isOpen}>
                                {group.placements.length === 0 ? (
                                    <Typography color="textSecondary" sx={{ px: 6, py: 2, fontSize: 14 }}>
                                        No placements in this category.
                                    </Typography>
                                ) : (
                                    group.placements.map((placement) => (
                                        <Box
                                            key={placement.id}
                                            className="flex items-center justify-between gap-3 py-2.5 pr-4"
                                            sx={{
                                                pl: 6,
                                                borderTop: "1px solid var(--border)",
                                                "&:hover": { backgroundColor: "var(--surface-2)" }
                                            }}
                                        >
                                            <Box className="flex items-center gap-2 min-w-0">
                                                <Typography sx={{ fontSize: 14 }} className="capitalize">
                                                    {placement.name || "Unnamed Placement"}
                                                </Typography>
                                                {placement.content_channel?.name && (
                                                    <Chip
                                                        label={placement.content_channel.name}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                            {canEdit && (
                                                <Box className="flex items-center gap-1">
                                                    <IconButton
                                                        size="small"
                                                        title="Edit placement"
                                                        onClick={() => {
                                                            setEditingPlacement(placement);
                                                            setShowPlacementModal(true);
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        title="Delete placement"
                                                        onClick={() => setPlacementToDelete(placement)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </Box>
                                    ))
                                )}
                            </Collapse>
                        </Box>
                    );
                })}
                {groups.length === 0 && (
                    <Typography color="textSecondary" className="py-8 text-center">
                        No placements found.
                    </Typography>
                )}
            </Box>

            <ContentPlacementModal
                open={showPlacementModal}
                onSave={handleSavePlacement}
                onClose={() => setShowPlacementModal(false)}
                initialData={
                    editingPlacement || {
                        name: "",
                        content_channel_id: "",
                        content_placement_category_id: ""
                    }
                }
            />

            <ContentPlacementCategoryModal
                open={showCategoryModal}
                onSave={handleSaveCategory}
                onClose={() => setShowCategoryModal(false)}
                // New categories default to the rmn flag of the active view.
                initialData={editingCategory || { name: "", rmn: view === 1 }}
            />

            <ConfirmationDialog
                open={!!placementToDelete}
                onClose={() => setPlacementToDelete(null)}
                onConfirm={handleDeletePlacement}
                title="Delete Placement"
                description="Are you sure you want to delete this placement?"
                confirmLabel="Delete"
            />

            <ConfirmationDialog
                open={!!categoryToDelete}
                onClose={() => setCategoryToDelete(null)}
                onConfirm={handleDeleteCategory}
                title="Delete Category"
                description="Are you sure you want to delete this category? Its placements will move to Uncategorized."
                confirmLabel="Delete"
            />
        </Box>
    );
};

export default PlacementsTab;
